import type { ReactNode } from "react";
import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { TenantLink } from "@src/features/tenants";

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <View style={styles.section}>
      <Text textStyle="md/semibold" color="textMuted">
        {title}
      </Text>
      <View gap="md">{children}</View>
    </View>
  );
}

/** One label/value row. A string value is selectable, so support can copy it into Patriot or a ticket. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <View style={styles.field}>
      <Text textStyle="xs/regular" color="textMuted" style={styles.fieldLabel}>
        {label}
      </Text>
      <View style={styles.fieldValue}>
        {typeof children === "string" ?
          <Text textStyle="sm/regular" color="text" selectable>
            {children}
          </Text>
        : children}
      </View>
    </View>
  );
}

export function Empty({ children }: { children: string }) {
  return (
    <Text textStyle="sm/regular" color="textMuted">
      {children}
    </Text>
  );
}

export function InlineLink({ href, children }: { href: Href; children: string }) {
  return (
    // Sized to its text, so the click target ends where the underline does.
    <TenantLink href={href} style={styles.inlineLink}>
      <Text textStyle="sm/regular" style={styles.link}>
        {children}
      </Text>
    </TenantLink>
  );
}

const styles = StyleSheet.create((theme) => ({
  section: {
    backgroundColor: theme.colors.surfaceCardInner,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    padding: theme.spacing["4xl"],
    gap: theme.spacing["2xl"],
  },
  field: { flexDirection: "row", alignItems: "baseline", gap: theme.spacing.xl },
  fieldLabel: { width: 110 },
  fieldValue: { flex: 1 },
  inlineLink: { alignSelf: "flex-start" },
  link: { color: theme.colors.interactive.buttons.secondary_foreground, textDecorationLine: "underline" },
}));
